/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import getConfig from '../config';
import Ingest from '@cchome/ingest-react';
import {getEnvironment} from '../utils/Environment';
import uuid from 'uuid';
import GMOData from '../utils/GMOMarketing';
import ProfileStorage from '../src/personalization/ProfileStorage';
import {linksLearnEntitle} from '../src/components/TopNav/constants';
import Url from './Url';
import {CCHomeAppsUtil} from 'cchome-apps';
import {fetchContext} from 'cchome-shared/utils';

let logMetricsCollection = 0;

/**
 * A class for initializing the Ingest module
 */
export default class AnalyticsUtil {
	ingest: ingestObject;
	contextInit: string | boolean | null;
	contextGuid: string | boolean | null;
	contextParams: string | boolean | null;
	static leftNavBarIndex: number;
	static feedbackCardScreenNumber: number;
	static showTopNav: boolean;
	static userBucket20181129Onboarding: string;
	static userBucket20181214Search: string;
	static userBucket20190224SuggestionsUI: string;
	static searchCampaignId: string;
	static searchVariationId: string;
	static searchPromptId: string;
	static deferredAnalytics: Array<any>;
	static currentCategory: string;
	static experiment: genericObject;

	constructor(withoutVisitorID?, useLocalReferrer?) {
		this.ingest = new Ingest(this.initAnalytics(withoutVisitorID, useLocalReferrer));

		const domain = getConfig('COOKIE_DOMAIN');
		const secure = getConfig('COOKIE_SECURE');
		[this.contextGuid, this.contextParams, this.contextInit] = fetchContext(uuid, domain, secure);
		logMetricsCollection = 0;

		if (window.logLearnMetrics && window.logLearnMetrics > 0) {
			logMetricsCollection = 1;
		}
	}

	/**
	 *
	 */
	initAnalytics(withoutVisitorID, useLocalReferrer) {
		const initObj: initAnalyticsObject = {};
		initObj.xApiKey = getConfig('INGEST').xApiKey;
		initObj.apiKey = getConfig('INGEST').apiKey;
		initObj.clientId = getConfig('CLIENT_ID');

		initObj.project = getConfig('INGEST').project;
		initObj.source = getConfig('INGEST').source;
		initObj.path = getConfig('INGEST').path;
		initObj.environment = getEnvironment(); // config.env;

		if (window.cchome.util && typeof window.cchome.util.optOutAnalytics === 'boolean') {
			initObj.optOutAnalytics = window.cchome.util.optOutAnalytics;
		}

		// Default to stage for all environments but 'production'
		initObj.hostName = getConfig('INGEST').hostNameStage;

		/* istanbul ignore else */
		if (initObj.environment !== 'prod') {
			initObj.hostName = getConfig('INGEST').hostNameStage;
		}
		else {
			initObj.hostName = getConfig('INGEST').hostNameProd;
		}
		if (withoutVisitorID) {
			initObj.timeOutForScripts = 0;
			initObj.sendWithOutVisitorID = true;
		}

		initObj.useLocalReferrer = true;
		initObj.referrerStorageName = 'event_referrer';
		if (typeof useLocalReferrer !== 'undefined') {
			initObj.useLocalReferrer = useLocalReferrer;
		}

		initObj.offline = false;

		this.setUserProfileInfo();

		return initObj;
	}

	/**
	 *
	 * @param {*} dataObject
	 */
	logAnalytics(dataObject: dataObject) {
		dataObject.setIngestContextGuid = this.contextGuid;
		if (this.contextParams) {
			dataObject.setIngestContextParams = this.contextParams;
		}
		dataObject.setIngestContextInit = this.contextInit;
		Object.keys(dataObject).forEach((key) => {
			const value = dataObject[key];
			if (value !== null && typeof value !== 'undefined') {
				switch (key) {
					case 'setEventCategory':
						this.ingest.setDataValue('event', 'category', value);
						break;
					case 'setEventPageName':
						this.ingest.setDataValue('event', 'pagename', value);
						break;
					case 'setEventErrorType':
						this.ingest.setDataValue('event', 'error_type', value);
						break;
					case 'setEventErrorDesc':
						this.ingest.setDataValue('event', 'error_desc', value);
						break;
					case 'setEventWorkFlow':
						this.ingest.setDataValue('event', 'workflow', value);
						break;
					case 'setEventWorkflow':
						this.ingest.setDataValue('event', 'workflow', value);
						break;
					case 'setEventSubcategory':
						this.ingest.setDataValue('event', 'subcategory', value);
						break;
					case 'setEventUrl':
						this.ingest.setDataValue('event', 'url', value);
						break;
					case 'setEventType':
						this.ingest.setDataValue('event', 'type', value);
						break;
					case 'setEventSubtype':
						this.ingest.setDataValue('event', 'subtype', value);
						break;
					case 'setEventCount':
						this.ingest.setDataValue('event', 'count', value);
						break;
					case 'setEventContextGUID':
						this.ingest.setDataValue('event', 'context_guid', value);
						break;
					case 'setEventGUID':
						this.ingest.setDataValue('event', 'guid', value);
						break;
					case 'setEventLanguage':
						this.ingest.setDataValue('event', 'language', value);
						break;
					case 'setEventContextSequence':
						this.ingest.setDataValue('event', 'context_sequence', value);
						break;
					case 'setEventID':
						this.ingest.setDataValue('event', 'id', value);
						break;
					case 'setOfflineStatus':
						this.ingest.setDataValue('event', 'offline', value);
						break;
					case 'setEventValue':
						this.ingest.setDataValue('event', 'value', value);
						break;
					case 'setConsumerClientId':
						this.ingest.setDataValue('consumer', 'client_id', value);
						break;
					case 'setConsumerName':
						this.ingest.setDataValue('consumer', 'name', value);
						break;
					case 'setContentID':
						this.ingest.setDataValue('content', 'id', value);
						break;
					case 'setContentName':
						this.ingest.setDataValue('content', 'name', value);
						break;
					case 'setContentType':
						this.ingest.setDataValue('content', 'type', value);
						break;
					case 'setContentCategory':
						this.ingest.setDataValue('content', 'category', value);
						break;
					case 'setContentAction':
						this.ingest.setDataValue('content', 'action', value);
						break;
					case 'setContentStatus':
						this.ingest.setDataValue('content', 'status', value);
						break;
					case 'setContentVersion':
						this.ingest.setDataValue('content', 'version', value);
						break;
					case 'setContentAuthor':
						this.ingest.setDataValue('content', 'author', value);
						break;
					case 'setUIFilter':
						this.ingest.setDataValue('ui', 'filter', value);
						break;
					case 'setUIViewType':
						this.ingest.setDataValue('ui', 'view_type', value);
						break;
					case 'setUISearchKeyword':
						this.ingest.setDataValue('ui', 'search_keyword', value);
						break;
					case 'setUISortOrder':
						this.ingest.setDataValue('ui', 'sort_order', value);
						break;
					case 'setEnvironmentServiceName':
						this.ingest.setDataValue('env', 'svc.name', value);
						break;
					case 'setEnvironmentServiceVersion':
						this.ingest.setDataValue('env', 'svc.version', value);
						break;
					case 'setEnvironmentComponentName':
						this.ingest.setDataValue('env', 'com.name', value);
						break;
					case 'setEnvironmentApiName':
						this.ingest.setDataValue('env', 'api.name', value);
						break;
					case 'setEnvironmentApiVersion':
						this.ingest.setDataValue('env', 'api.version', value);
						break;
					case 'setExpCampaignID':
						this.ingest.setDataValue('exp', 'campaign_id', value);
						break;
					case 'setExpVariationID':
						this.ingest.setDataValue('exp', 'variation_id', value);
						break;
					case 'setExpRequestGUID':
						this.ingest.setDataValue('exp', 'request_guid', value);
						break;
					case 'setExpResponseGUID':
						this.ingest.setDataValue('exp', 'response_guid', value);
						break;
					case 'setExpSurfaceID':
						this.ingest.setDataValue('exp', 'surface_id', value);
						break;
					case 'setExpActionBlockID':
						this.ingest.setDataValue('exp', 'action_block_id', value);
						break;
					case 'setExpContainerID':
						this.ingest.setDataValue('exp', 'container_id', value);
						break;
					case 'setExpTreatmentID':
						this.ingest.setDataValue('exp', 'treatment_id', value);
						break;
					case 'setExpControlGroupID':
						this.ingest.setDataValue('exp', 'control_group_id', value);
						break;
					case 'setExpExperienceID':
						this.ingest.setDataValue('exp', 'experience_id', value);
						break;
					case 'setTrnNumber':
						this.ingest.setDataValue('trn', 'number', value);
						break;
					case 'setTrnProduct':
						this.ingest.setDataValue('trn', 'product', value);
						break;
					case 'setTrnQuantity':
						this.ingest.setDataValue('trn', 'quantity', value);
						break;
					case 'setTrnAmount':
						this.ingest.setDataValue('trn', 'amount', value);
						break;
					case 'setIngestContextGuid':
						this.ingest.setDataValue('context', 'guid', value);
						break;
					case 'setIngestContextParams':
						this.ingest.setDataValue('context', 'params', value);
						break;
					case 'setIngestContextInit':
						this.ingest.setDataValue('context', 'init', value);
						break;
					default:
						window.cchome.util.logger.error('Analytics Util', 'Invalid key for Learning Service', key);
						break;
				}
			}
		});

		this.ingest.addToCollection();
	}

	/**
	 * do we have an object
	 * @param {*} obj
	 */
	objectExists(obj) {
		if (typeof obj !== 'undefined' && obj !== null) {
			return true;
		}
		return false;
	}

	/**
	 * The name of the profile storage could change.
	 * So here we get it using ProfileStorage.getProfileStorageKey
	 * and then get the information we are interested in, and create
	 * an entry into session storage 'subscriptionPlan' that the
	 * ingest module will always use.
	 */
	setUserProfileInfo() {
		if (navigator.cookieEnabled && window.cchome.util.storageAvailable('sessionStorage')) {
			const cachedProfile: string | null = sessionStorage.getItem(ProfileStorage.getProfileStorageKey());

			let cachedProfileObj: genericObject = {};
			if (cachedProfile && cachedProfile.length > 0) {
				try {
					cachedProfileObj = JSON.parse(cachedProfile);
				}
				catch (e) {
					cachedProfileObj = {};
				}
			}

			if (
				this.objectExists(cachedProfileObj)
				&& Object.keys(cachedProfileObj).length !== 0
				&& typeof cachedProfileObj.subscription !== 'undefined'
			) {
				/* eslint-disable */
				if (typeof cachedProfileObj.subscription.planCode !== 'undefined') {
					let plan = cachedProfileObj.subscription.planCode;
					if (plan === 'unknown') {
						if (cachedProfileObj.subscription.status) {
							plan = cachedProfileObj.subscription.status;
						}
					}

					sessionStorage.setItem('subscriptionPlan', plan);
				}

				// if (typeof cachedProfileObj.subscription.stakeholder !== 'undefined') {
				// 	const isStakeHolder = cachedProfileObj.subscription.stakeholder;
				// 	sessionStorage.setItem('stakeholder', isStakeHolder);

				/* eslint-enable */
			}
		}
	}

	static appendUrlWithAnalytics(inputUrl: string, isStock = false) {
		// https://wiki.corp.adobe.com/pages/viewpage.action?pageId=1444405515

		let outputUrl = '';
		if (typeof outputUrl === 'string') {
			outputUrl = inputUrl;
		}

		const params: genericObject = {
			'x-product': `${getConfig('APP_NAME')}/${getConfig('APP_VERSION')}`,
			guid: uuid.v4(),
		};

		const location = this.getLeftTab();
		if (typeof location !== 'undefined' && location.length) {
			params['x-location'] = location;
		}

		/* eslint-disable camelcase */
		if (isStock) {
			params.as_channel = 'adobe_apps';
			params.as_campclass = 'brand';
			params.as_source = 'app';
			params.as_camptype = 'CCHome';
		}

		/* eslint-enable camelcase */

		const encodeGetParams = p =>
			Object.entries(p)
				.map((kv: any) => kv.map(encodeURIComponent).join('='))
				.join('&');

		outputUrl = outputUrl.concat((outputUrl.split('?')[1] ? '&' : '?') + encodeGetParams(params));

		return outputUrl;
	}

	/**
	 * Before we send the analytics, make sure the user
	 * has allowed us to do so. To ensure this, we need to
	 * makae a network call to PIIP. There can be some latency
	 * here, so until we get the response, we will cache the events
	 * in 'deferredAnalytics'
	 *
	 * @param {Contains all the analytics info} ingestObj
	 */
	checkOptin(ingestObj: any) {
		// Some analytics can contain the user's name.
		// We don't want those going to our intent server
		// If the 'sendIntent' flag is set, honor it, else default
		// sending to intent server.

		if (window.cchome.util.optOutAnalyticsInited && !window.cchome.util.optOutAnalytics) {
			if (typeof ingestObj.sendIntent === 'boolean') {
				this.sendToIngest(ingestObj, ingestObj.sendIntent);
			}
			else {
				this.sendToIngest(ingestObj, true);
			}
		}

		if (!window.cchome.util.optOutAnalyticsInited) {
			// Create and obj the contains flag and array of
			// events.
			const metricObj: genericObject = {sendIntent: true, metricArray: []};

			if (typeof ingestObj.sendIntent === 'boolean') {
				metricObj.sendIntent = ingestObj.sendIntent;
			}
			if (typeof ingestObj.metricsCollection !== 'undefined') {
				ingestObj.metricsCollection.forEach((metric) => {
					metricObj.metricArray.push(metric);
				});
			}

			AnalyticsUtil.deferredAnalytics.push(metricObj);
		}
	}

	/**
	 * This function will send the events to ingest and intent server
	 * when intent flag is true.
	 * @param {*} ingestObj
	 */
	sendToIngest(ingestObj: ingestObject, sendIntent) {
		if (typeof ingestObj.metricsCollection !== 'undefined' && ingestObj.metricsCollection.length) {
			if (logMetricsCollection) {
				const arrayLength = ingestObj.metricsCollection.length;
				for (let i = 0; i < arrayLength; i++) {
					console.log('sendToIngest metricsCollection = ', ingestObj.metricsCollection[i].events[0].data);
				}
			}

			ingestObj.sendToIngest();

			// send the events to the CCHome intents server also
			if (sendIntent && window.eventsService) {
				window.eventsService.reportEvents(ingestObj.metricsCollection);
			}
		}
	}

	/**
	 * This function is called when
	 * const customEvent = new CustomEvent('analyticsOptinInited');
	 * window.dispatchEvent(customEvent);
	 * is involked.
	 *
	 * We will check for any cached analytics and send them
	 * if user has not opt'ed out.
	 */
	static sendDeferredMetric() {
		if (
			!window.cchome.util.optOutAnalytics
			&& AnalyticsUtil.deferredAnalytics
			&& AnalyticsUtil.deferredAnalytics.length > 0
		) {
			const analytics = new AnalyticsUtil();
			AnalyticsUtil.deferredAnalytics.forEach((metricObj) => {
				metricObj.metricArray.forEach((metric) => {
					analytics.ingest.metricsCollection.push(metric);
				});
				analytics.sendToIngest(analytics.ingest, metricObj.sendIntent);
				analytics.ingest.metricsCollection = [];
			});
		}
		AnalyticsUtil.deferredAnalytics = [];
	}

	/**
	 * Get location via url
	 */
	getLocation(): string {
		let location = 'Home';
		const firstPath = window.location.pathname.split('/')[1];

		switch (firstPath.toLowerCase()) {
			case 'apps':
				location = 'Apps';
				break;
			case 'learn':
				location = 'Learn';
				break;
			default:
				location = 'Home';
				break;
		}
		return location;
	}

	/**
	 * Get the current location of CCHome
	 */
	static getLeftTab() {
		let tab
			= linksLearnEntitle[AnalyticsUtil.leftNavBarIndex] && linksLearnEntitle[AnalyticsUtil.leftNavBarIndex].tab;
		if (typeof tab !== 'undefined' && tab.length) {
			tab = tab.toLowerCase();
			tab = tab.replace('_tab', '');
			tab = tab.replace('_', ' ');
			tab = `${tab.charAt(0).toUpperCase()}${tab.slice(1)}`;

			// In the case of Your Work we need to capitalize the 'W'
			const index = tab.indexOf(' ');
			if (index >= 0 && tab.length > index) {
				const char = tab.charAt(index + 1).toUpperCase();
				tab = `${tab.substr(0, index + 1)}${char}${tab.substr(index + 2)}`;
			}
		}

		return tab;
	}

	/**
	 * Send one event for each feature flag
	 * @param {*} featureFlag
	 * @param {*} subtype
	 * @param {string} url the url the user is redirected to if they're not eligible
	 */
	eligibilityAnalytics(featureFlag: string, subtype: boolean, url: string) {
		this.ingest.initializeDefaultData();

		const data: dataObject = {
			setEventWorkFlow: 'Release',
			setEventSubcategory: 'Split',
			setEventType: 'split',
			setEventSubtype: subtype,
			setContentAction: url,
		};

		data.setExpCampaignID = featureFlag;

		if (CCHomeAppsUtil.isCCHInLMS()) {
			// To set the analytics parameters when apps being opened in Canvas
			this._analyticsForCCWInLMS();
		}

		this.logAnalytics(data);

		this.checkOptin(this.ingest);
	}

	/**
	 *
	 * @param {is user eligible} subtype
	 * @param {string} url the url the user is redirected to if they're not eligible
	 */
	postEligibilityAnalytics(subtype: boolean, url: string) {
		this.eligibilityAnalytics('CCH-CN', subtype, url);
	}

	/**
	 * When user clicks/renders main page
	 * @param {*} action
	 */
	postMainAction(action, isDefault) {
		this.ingest.initializeDefaultData();

		const tab = AnalyticsUtil.getLeftTab();
		const data: dataObject = {
			setEventWorkFlow: tab,
			setEventSubcategory: 'Main',
			setEventType: action,
			setEventSubtype: 'page',
		};

		if (action === 'render') {
			data.setEventValue = isDefault;
		}

		if (tab === 'Apps' && CCHomeAppsUtil.isCCHInLMS()) {
			// To set the analytics parameters when apps being opened in Canvas
			this._analyticsForCCWInLMS();
		}

		this.logAnalytics(data);
		this.checkOptin(this.ingest);

		const gmoData = new GMOData();
		gmoData.dunamisToGMOData(this.ingest.metricsCollection, action);
	}

	/**
	 * When Initial Config Service resolve with empty FF array
	 */
	sendEmptyFFAnalytic(errorDesc: string) {
		this.ingest.initializeDefaultData();
		const data: dataObject = {
			setEventWorkFlow: 'Home',
			setEventType: 'api',
			setEventSubtype: 'ff-error',
			setEventErrorType: 'config-feature-flag',
			setEventErrorDesc: errorDesc,
		};

		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	sendGrowthThroughlineHelpAnalytics(url: string) {
		let campaignId = '2019-09-IntentSelection';
		let app = 'ccpp';

		const isSinglePs = Url.getParam('isSinglePs') !== null ? Url.getParam('isSinglePs') : '';
		const throughlineType = Url.getParam('type') !== null ? Url.getParam('type') : '';

		if (isSinglePs === 'true') {
			campaignId = '2019-12-IntentSelection-nonTWP';
			app = 'photoshop';
		}
		else if (throughlineType === 'pstrialEn') {
			campaignId = '2020-04-IntentSelection-PS-TWP';
			app = 'photoshop';
		}
		else if (throughlineType === 'photoJa') {
			campaignId = '2020-04-IntentSelection-PS-CCPP-JA';
			app = Url.getParam('app') !== null && Url.getParam('app') === 'photoshop' ? 'photoshop' : 'ccpp';
		}

		this.ingest.initializeDefaultData();
		const data = {
			setEventSubcategory: 'top-nav',
			setEventWorkFlow: 'Intent-Selection',
			setEventType: 'click',
			setEventSubtype: 'help',
			setContentAction: url,
			setExpCampaignID: campaignId,
			setExpVariationID: 'test',
			setEnvironmentComponentName: app,
		};

		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	sendNavBarLogoClickAnalytics(eventData) {
		const {workflow, subtype, type, envSvcName, envSvcVersion, uiViewType, eventSubcategory} = eventData || {};
		this.ingest.initializeDefaultData();
		const data: dataObject = {
			setEventWorkFlow: workflow || 'Navigation',
			setEventType: type || 'click-disabled',
			setEventSubtype: subtype || 'navbar-logo',
			setEventSubcategory: eventSubcategory,
		};
		if (envSvcName) {
			data.setEnvironmentServiceName = envSvcName;
		}
		if (envSvcVersion) {
			data.setEnvironmentServiceVersion = envSvcVersion;
		}
		if (uiViewType) {
			data.setUIViewType = uiViewType;
		}
		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	sendAppPickerUtilNavAnalytics(subCategory: string | null | undefined, campaignId: string) {
		this.ingest.initializeDefaultData();
		const data: dataObject = {
			setEventWorkFlow: 'App Picker',
			setEventType: 'click',
			setEventSubtype: 'util-nav-a-com',
			setEventSubcategory: subCategory,
			setExpCampaignID: campaignId,
			setExpVariationID: 'test',
			setContentAuthor: 'designer',
		};
		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	sendFeedbackSuppressedAnalytics(feedbackType: string, uiViewType: string) {
		this.ingest.initializeDefaultData();
		const data: dataObject = {
			setEventWorkFlow: 'App Picker',
			setEventType: 'render',
			setEventSubtype: 'modal',
			setEventSubcategory: 'Feedback',
			setExpCampaignID: '201908-App-Picker-Feedback',
			setExpVariationID: 'test',
			setContentAuthor: 'designer',
			setContentName: 'Can we help you find something',
			setUIFilter: 'Adobe Logo Feedback',
			setContentType: 'adobe-logo',
			setUIViewType: uiViewType,
			setEventValue: 'suppressed',
		};
		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	sendRedirectAnalytic(eventType, url, reason, errordesc) {
		// TODO MDS
		this.ingest.initializeDefaultData();
		const data: dataObject = {
			setEventWorkFlow: 'Home',
			setEventType: eventType,
			setEventSubtype: reason,
			setContentAction: url,
		};
		if (errordesc) {
			data.setEventErrorDesc = errordesc;
		}
		this.logAnalytics(data);
		return this.ingest.sendToIngest();

		// if (typeof this.ingest.metricsCollection !== 'undefined' && this.ingest.metricsCollection.length) {
		// 	if (window.eventsService) {
		// 		window.eventsService.reportEvents(this.ingest.metricsCollection);
		// 	}
		// }
	}

	/**
	 * When CCH Logged Out Exp main page renders
	 * @param {*} action
	 */
	postMainCCHLOERender() {
		this.ingest.initializeDefaultData();

		const data: dataObject = {
			setEventWorkFlow: 'CC Downloads',
			setEventSubcategory: 'Main',
			setEventType: 'render',
			setEventSubtype: 'page',
			setEventValue: true,
		};

		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	/**
	 * When we detect promotion type (offer_type) send back from JIL to Profile API. SDM > cchome-web > DT column
	 * @param {string} offerTypeValue value from JIL for offer type
	 * @param {string} url JIL API
	 */
	postOfferType(offerTypeValue, url) {
		this.ingest.initializeDefaultData();

		const data = {
			setEventWorkFlow: 'Operational',
			setEventSubcategory: 'Profile Attributes',
			setEventType: 'implicit',
			setEventSubtype: 'offer_type',
			setEventValue: offerTypeValue,
			setEnvironmentApiName: url,
		};

		this.logAnalytics(data);
		this.checkOptin(this.ingest);
	}

	/**
	 * Clear out event_referrer at start up to get rid of cruft
	 */
	static removeReferrer() {
		window.cchome.util.removeReferrer();
	}

	/**
	 * Set event_referrer in local stoage for analytics. This information is used
	 * in the Ingest module
	 * @param {*} prevLocation
	 */
	static setReferrer(prevLocation: string) {
		window.cchome.util.setReferrer(prevLocation);
	}

	/** private methods
	 *
	 */
	_analyticsForCCWInLMS() {
		if (window.isCCHInLMSCanvas) {
			this.ingest.setDataValue('env', 'com.name', 'Canvas');
		}
		else if (window.isCCHInLMSBlackboard) {
			this.ingest.setDataValue('env', 'com.name', 'Blackboard');
		}
		this.ingest.setDataValue('event', 'url', CCHomeAppsUtil.updateAnalyticsUrlForCanvas());
	}
}

AnalyticsUtil.currentCategory = ' ';
AnalyticsUtil.leftNavBarIndex = 0;
AnalyticsUtil.showTopNav = true;
AnalyticsUtil.userBucket20181129Onboarding = 'control';
AnalyticsUtil.userBucket20181214Search = '';
AnalyticsUtil.userBucket20190224SuggestionsUI = '';
AnalyticsUtil.experiment = {};
AnalyticsUtil.searchCampaignId = '';
AnalyticsUtil.searchVariationId = '';
AnalyticsUtil.searchPromptId = '';
AnalyticsUtil.deferredAnalytics = [];
